import { Tabs, Stack, Text, Box, Title, List, Code, Table, Alert, Badge, Group, Card, ThemeIcon } from '@mantine/core'
import {
  IconShieldCheck,
  IconClock,
  IconChecklist,
  IconBan,
  IconBraces,
  IconKey,
  IconScale,
  IconHash,
  IconInfoCircle,
} from '@tabler/icons-react'

const EVENT_JSON_EXAMPLE = `{
  "id": 184729,
  "tenant_id": 5,
  "actor_user_id": 42,
  "actor_user_email": "carlos.farah@amazonasinovare.com.br",
  "actor_user_name": "Carlos Farah",
  "actor_roles": ["admin"],
  "event_type": "rbac",
  "action": "rbac:template_update",
  "resource_type": "permission_template",
  "resource_id": "12",
  "severity": "info",
  "outcome": "success",
  "client_ip": "201.10.20.30",
  "user_agent": "Mozilla/5.0...",
  "request_id": "01J9X8Q3...",
  "http_method": "PUT",
  "http_path": "/api/v1/permission-templates/12",
  "http_status": 200,
  "duration_ms": 47,
  "before_state": null,
  "after_state": null,
  "payload": {
    "name": "Operador Junior",
    "permission_count": 14,
    "tenant_ids": [5, 7]
  },
  "prev_event_hash": "8a3f5d2c...",
  "event_hash": "f1b9e0a7...",
  "archived_at": null,
  "created_at": "2026-04-29T07:08:17Z"
}`

const COVERAGE: { domain: string; events: string; description: string }[] = [
  { domain: 'IAM', events: 'users:* · roles:* · registration:*', description: 'Cadastro, edição, deleção e atribuição de roles a usuários.' },
  { domain: 'RBAC', events: 'rbac:template_* · rbac:apply_template* · rbac:set_user_permissions', description: 'Templates de permissão e atribuições diretas.' },
  { domain: 'Profile', events: 'profile:update · profile:change_password', description: 'Atualizações próprias do usuário, incluindo troca de senha UNIX (Kanidm).' },
  { domain: 'LDAP/Kanidm', events: 'ldap:* (persons + groups + ssh-keys + unix)', description: 'Shadow audit espelhado em audit.events além do log isolado ldap_audit_log.' },
  { domain: 'GMUD', events: 'gmud:create · submit · cab_vote · schedule · execution · ratify', description: 'Lifecycle completo de Gestão de Mudança.' },
  { domain: 'Organizations', events: 'organization:create · update · delete · add_member · add_user', description: 'Mudanças estruturais multi-tenant.' },
  { domain: 'Tenants', events: 'tenant:admin_update', description: 'Alteração de flags (has_terraform/has_waf/has_pci/has_pentest/group_id).' },
  { domain: 'Hard Restart EC2/Docker', events: 'docker:hard_restart · ec2:restart · ec2:deploy', description: 'Operações destrutivas em infra (kick-off + outcome).' },
  { domain: 'S3', events: 's3:create_bucket · s3:upload_object · s3:delete_object', description: 'GDPR/LGPD: alta sensibilidade.' },
  { domain: 'Secrets Manager', events: 'secrets:put_value', description: 'Atualização de valor de secret AWS.' },
  { domain: 'Jira', events: 'jira:config_* · org_link · org_toggle · webhook_* · status_mapping · user_mapping · ticket_link · bulk_sync · custom_field_mappings', description: 'Configurações + ops state-changing.' },
  { domain: 'GitLab', events: 'gitlab:admin_configure · admin_disable · register_project · unregister_project', description: 'Tenant config + project mappings.' },
  { domain: 'Zabbix', events: 'zabbix:admin_toggle · admin_remove_config · config_toggle · config_delete', description: 'Configurações por tenant e admin.' },
  { domain: 'WhatsApi', events: 'whatsapi:admin_toggle', description: 'Habilitação por tenant.' },
  { domain: 'Workflow / Chamados', events: 'chamados:create · status_transition · assign · review · comment_create · attachment_* · category_* · alert_rule_* · form_template_* · csat', description: 'Lifecycle de chamados, anexos, categorias, regras de alerta e templates de formulário.' },
  { domain: 'Budget', events: 'budget:upsert', description: 'Threshold de budget e alert percentage.' },
  { domain: 'Audit Grants (meta)', events: 'audit:grant_request · grant_approve · grant_reject · purge_completed', description: 'O próprio sistema de auditoria é auditado.' },
  { domain: 'Provisioning', events: 'user_provisioned (event_type=provisioning)', description: 'Provisionamento de novos usuários (Kanidm + DB + roles).' },
]

const NOT_AUDITED: { domain: string; reason: string }[] = [
  { domain: 'Endpoints GET (read-only)', reason: 'Leitura pura; sem mudança de estado para preservar evidência.' },
  { domain: 'Cache / refresh ops (zabbix_refresh, dashboard_refresh, dashboard_cache_update)', reason: 'Operações idempotentes de cache; volume alto, sinal baixo.' },
  { domain: 'Notifications mark-as-read / mark-all', reason: 'Self-service do usuário em sua própria caixa de notificações.' },
  { domain: 'External webhooks (GitLab push/MR/deploy, Jira webhook callback)', reason: 'Eventos vindos de fora do ConsoleAI; já têm logs estruturados próprios.' },
  { domain: 'Sync triggers low-risk (jira:sync_trigger, sync_single_ticket)', reason: 'Operações de polling sem mudança de estado direta.' },
  { domain: 'CSAT submit (anônimo)', reason: 'Avaliação anônima fora do contexto autenticado.' },
  { domain: 'Sub-tasks CRUD', reason: 'Alta volumetria; baixo risco; coberto indiretamente pelo audit do chamado pai.' },
  { domain: 'Permissions validate (trigger async)', reason: 'Disparo de validação periódica; resultado vai para outra fonte.' },
]

const PERMISSIONS: { perm: string; scope: string; quem: string }[] = [
  { perm: 'audit:read', scope: 'Eventos do tenant atual', quem: 'Usuários com permissão padrão de auditoria no tenant.' },
  { perm: 'audit:read_global', scope: 'Eventos cross-tenant', quem: 'Apenas role auditor_global (cross-tenant).' },
  { perm: 'audit:write', scope: 'Aprovar/rejeitar grant requests', quem: 'auditor_global + 2-step approval (audit grants).' },
]

export function AuditoriaPoliticas() {
  return (
    <Stack gap="lg" p="md" maw={1200} mx="auto">
      <Group gap="sm">
        <ThemeIcon size="xl" radius="md" variant="light" color="teal">
          <IconShieldCheck size={28} />
        </ThemeIcon>
        <Box>
          <Title order={2}>Políticas de Auditoria</Title>
          <Text c="dimmed" size="sm">
            Como o ConsoleAI registra, protege, retém e expurga eventos de auditoria.
          </Text>
        </Box>
      </Group>

      <Tabs defaultValue="overview" variant="default" keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<IconShieldCheck size={16} />}>Visão geral</Tabs.Tab>
          <Tabs.Tab value="retention" leftSection={<IconClock size={16} />}>Retenção e expurgo</Tabs.Tab>
          <Tabs.Tab value="coverage" leftSection={<IconChecklist size={16} />}>O que é auditado</Tabs.Tab>
          <Tabs.Tab value="exceptions" leftSection={<IconBan size={16} />}>O que NÃO é</Tabs.Tab>
          <Tabs.Tab value="event" leftSection={<IconBraces size={16} />}>Estrutura do evento</Tabs.Tab>
          <Tabs.Tab value="access" leftSection={<IconKey size={16} />}>Acesso e permissões</Tabs.Tab>
          <Tabs.Tab value="compliance" leftSection={<IconScale size={16} />}>Compliance</Tabs.Tab>
          <Tabs.Tab value="integrity" leftSection={<IconHash size={16} />}>Integridade</Tabs.Tab>
        </Tabs.List>

        <Box pt="md">
          <Tabs.Panel value="overview">
            <Stack gap="md">
              <Title order={3}>Princípios</Title>
              <Text>
                Toda ação sensível no ConsoleAI gera um evento imutável em <Code>audit.events</Code> (PostgreSQL).
                A trilha foi desenhada para resistir a tampering, ser cross-checkável por auditores externos
                e atender LGPD, SOX e PCI-DSS req. 10.
              </Text>

              <Card withBorder padding="md" radius="md">
                <Stack gap="xs">
                  <Group gap="xs">
                    <IconHash size={18} />
                    <Text fw={600}>Hash chain SHA-256</Text>
                  </Group>
                  <Text size="sm" c="dimmed">
                    Cada evento contém <Code>event_hash</Code> calculado sobre todo o conteúdo + <Code>prev_event_hash</Code> (hash do evento anterior do mesmo tenant). Romper qualquer
                    elo é detectável via <Code>POST /api/v1/audit/verify-chain</Code>.
                  </Text>
                </Stack>
              </Card>

              <Card withBorder padding="md" radius="md">
                <Stack gap="xs">
                  <Group gap="xs">
                    <IconShieldCheck size={18} />
                    <Text fw={600}>Append-only</Text>
                  </Group>
                  <Text size="sm" c="dimmed">
                    A role do app no Postgres tem apenas <Code>INSERT</Code> em <Code>audit.events</Code>. UPDATE/DELETE só ocorrem por workers internos (purge mensal) que respeitam o pré-requisito de já ter sido arquivado em S3.
                  </Text>
                </Stack>
              </Card>

              <Card withBorder padding="md" radius="md">
                <Stack gap="xs">
                  <Group gap="xs">
                    <IconBraces size={18} />
                    <Text fw={600}>Self-healing de identidade</Text>
                  </Group>
                  <Text size="sm" c="dimmed">
                    Quando um call site grava evento sem informar email/nome/roles do ator, o serviço hidrata
                    automaticamente do banco a partir do <Code>actor_user_id</Code>, evitando linhas com ator desconhecido na UI.
                  </Text>
                </Stack>
              </Card>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="retention">
            <Stack gap="md">
              <Title order={3}>Retenção em 2 camadas</Title>
              <Text>
                Janela total de <strong>3 anos</strong> de evidência auditável: 13 meses online em Postgres + 36 meses arquivado em S3 com Object Lock.
              </Text>

              <Table withTableBorder withColumnBorders striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Camada</Table.Th>
                    <Table.Th>Tempo</Table.Th>
                    <Table.Th>Worker</Table.Th>
                    <Table.Th>Frequência</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  <Table.Tr>
                    <Table.Td><Badge color="teal">Postgres (online)</Badge></Table.Td>
                    <Table.Td>13 meses</Table.Td>
                    <Table.Td><Code>audit_purge_worker</Code></Table.Td>
                    <Table.Td>Mensal — dia 1 às 04:00 UTC</Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td><Badge color="indigo">S3 (Object Lock)</Badge></Table.Td>
                    <Table.Td>36 meses (1095 dias)</Table.Td>
                    <Table.Td><Code>audit_archive_worker</Code></Table.Td>
                    <Table.Td>Contínuo — a cada 60s</Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>

              <Alert color="yellow" icon={<IconInfoCircle size={18} />} title="Proteção contra perda">
                O purge do Postgres só apaga eventos com <Code>archived_at IS NOT NULL</Code>. Se o archive worker estiver fora do ar ou o bucket S3 inacessível, eventos antigos NÃO são removidos do Postgres.
              </Alert>

              <Title order={4} mt="md">Variáveis de ambiente</Title>
              <Table withTableBorder>
                <Table.Tbody>
                  <Table.Tr>
                    <Table.Td><Code>AUDIT_S3_BUCKET</Code></Table.Td>
                    <Table.Td>Bucket de destino (obrigatório para archive ativar).</Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td><Code>AUDIT_S3_KMS_KEY_ID</Code></Table.Td>
                    <Table.Td>KMS key para encriptar batches no S3.</Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td><Code>AUDIT_S3_RETENTION_DAYS</Code></Table.Td>
                    <Table.Td>Default 1095 (36 meses). Object Lock retain-until.</Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td><Code>AUDIT_BATCH_SIZE</Code></Table.Td>
                    <Table.Td>Default 1000 eventos por batch.</Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="coverage">
            <Stack gap="md">
              <Title order={3}>Cobertura por domínio</Title>
              <Text>
                Aproximadamente <strong>100 endpoints state-changing</strong> registram em <Code>audit.events</Code>. Lista por domínio:
              </Text>

              <Table withTableBorder withColumnBorders striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={180}>Domínio</Table.Th>
                    <Table.Th>Action namespaces</Table.Th>
                    <Table.Th>Descrição</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {COVERAGE.map((c) => (
                    <Table.Tr key={c.domain}>
                      <Table.Td><Text fw={600}>{c.domain}</Text></Table.Td>
                      <Table.Td><Code>{c.events}</Code></Table.Td>
                      <Table.Td><Text size="sm" c="dimmed">{c.description}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="exceptions">
            <Stack gap="md">
              <Title order={3}>O que NÃO é auditado</Title>
              <Text>
                Decisão consciente para preservar sinal/ruído. Toda exclusão é justificada:
              </Text>

              <Table withTableBorder withColumnBorders striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={320}>Domínio / endpoint</Table.Th>
                    <Table.Th>Por quê</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {NOT_AUDITED.map((n) => (
                    <Table.Tr key={n.domain}>
                      <Table.Td><Text fw={500}>{n.domain}</Text></Table.Td>
                      <Table.Td><Text size="sm" c="dimmed">{n.reason}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>

              <Alert color="blue" icon={<IconInfoCircle size={18} />}>
                Se sua função de SI exigir auditar algum desses, abra um chamado em <Code>infra/recursos</Code> com justificativa de compliance — adicionamos.
              </Alert>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="event">
            <Stack gap="md">
              <Title order={3}>Estrutura de um evento</Title>
              <Text>
                Cada linha em <Code>audit.events</Code> contém os campos abaixo. Payloads sensíveis (passwords, tokens, secrets) são automaticamente sanitizados como <Code>"***"</Code>.
              </Text>

              <Code block style={{ fontSize: 12, lineHeight: 1.5 }}>
                {EVENT_JSON_EXAMPLE}
              </Code>

              <Title order={4} mt="md">Campos principais</Title>
              <List spacing="xs" size="sm">
                <List.Item><Code>actor_*</Code>: identidade completa do ator no momento do evento (snapshot).</List.Item>
                <List.Item><Code>action</Code>: namespaced (<Code>domain:operation</Code>) — ex: <Code>chamados:create</Code>.</List.Item>
                <List.Item><Code>resource_type</Code> + <Code>resource_id</Code>: o que foi afetado.</List.Item>
                <List.Item><Code>before_state</Code> / <Code>after_state</Code>: snapshots antes/depois (quando aplicável).</List.Item>
                <List.Item><Code>payload</Code>: dados específicos da operação (sanitizados).</List.Item>
                <List.Item><Code>severity</Code>: <Code>info</Code> | <Code>warning</Code> | <Code>critical</Code>.</List.Item>
                <List.Item><Code>outcome</Code>: <Code>success</Code> | <Code>failure</Code>.</List.Item>
                <List.Item><Code>client_ip</Code> + <Code>user_agent</Code> + <Code>request_id</Code>: rastreamento de origem.</List.Item>
                <List.Item><Code>http_method</Code> + <Code>http_path</Code> + <Code>http_status</Code>: contexto da request.</List.Item>
                <List.Item><Code>prev_event_hash</Code> + <Code>event_hash</Code>: encadeamento criptográfico.</List.Item>
                <List.Item><Code>archived_at</Code> + <Code>archive_uri</Code>: rastreamento do batch S3 quando arquivado.</List.Item>
              </List>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="access">
            <Stack gap="md">
              <Title order={3}>Acesso e permissões</Title>

              <Table withTableBorder withColumnBorders striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Permissão</Table.Th>
                    <Table.Th>Escopo</Table.Th>
                    <Table.Th>Quem tem</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {PERMISSIONS.map((p) => (
                    <Table.Tr key={p.perm}>
                      <Table.Td><Code>{p.perm}</Code></Table.Td>
                      <Table.Td><Text size="sm">{p.scope}</Text></Table.Td>
                      <Table.Td><Text size="sm" c="dimmed">{p.quem}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>

              <Title order={4} mt="md">Workflow de Audit Grant</Title>
              <Text size="sm">
                Para conceder a alguém visibilidade global temporária:
              </Text>
              <List type="ordered" spacing="xs" size="sm">
                <List.Item>Solicitante chama <Code>POST /api/v1/audit/grants/requests</Code> com <Code>target_user_id</Code>, <Code>role_code</Code>, <Code>reason</Code>.</List.Item>
                <List.Item>Auditor com <Code>auditor_global</Code> aprova via <Code>POST /api/v1/audit/grants/{'{id}'}/approve</Code> ou rejeita via <Code>POST /audit/grants/{'{id}'}/reject</Code>.</List.Item>
                <List.Item>Toda etapa gera evento <Code>audit:grant_*</Code> (meta-audit).</List.Item>
              </List>

              <Alert color="teal" icon={<IconInfoCircle size={18} />} title="Tela de Auditoria por tenant">
                A tela <Code>/auditoria</Code> é <strong>tenant-scoped</strong> — mostra apenas eventos do tenant
                ativo. Ações globais (RBAC, profile, organizations, audit grants) ficam sob <Code>tenant_id=1</Code>.
                Para visão cross-tenant use <Code>/auditoria-global</Code> (requer <Code>auditor_global</Code>).
              </Alert>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="compliance">
            <Stack gap="md">
              <Title order={3}>Conformidade regulatória</Title>

              <Card withBorder padding="md" radius="md">
                <Group gap="xs" mb="xs">
                  <Badge color="grape">LGPD</Badge>
                  <Text fw={600}>Lei Geral de Proteção de Dados</Text>
                </Group>
                <Text size="sm" c="dimmed">
                  Art. 6º (rastreabilidade) e Art. 37 (registro de operações de tratamento). Toda operação
                  envolvendo dados pessoais (criação/edição de usuário, reset de senha, atribuição de role,
                  acesso a ticket com PII) é registrada com identidade completa do ator + timestamp.
                </Text>
              </Card>

              <Card withBorder padding="md" radius="md">
                <Group gap="xs" mb="xs">
                  <Badge color="blue">SOX-friendly</Badge>
                  <Text fw={600}>Sarbanes-Oxley Section 404</Text>
                </Group>
                <Text size="sm" c="dimmed">
                  Hash chain SHA-256 garante integridade das evidências. Tampering retrospectivo é detectável
                  via verificação de chain. Workers de retenção também são auditados (<Code>audit:purge_completed</Code>).
                </Text>
              </Card>

              <Card withBorder padding="md" radius="md">
                <Group gap="xs" mb="xs">
                  <Badge color="orange">PCI-DSS</Badge>
                  <Text fw={600}>Requirement 10 — Track and monitor access</Text>
                </Group>
                <Text size="sm" c="dimmed">
                  10.7: retenção mínima de 1 ano online + arquivamento adicional. Atendido com folga (13 meses online + 36 meses S3 = 49 meses totais).
                </Text>
              </Card>

              <Card withBorder padding="md" radius="md">
                <Group gap="xs" mb="xs">
                  <Badge color="teal">Self-audit</Badge>
                  <Text fw={600}>O sistema audita a si mesmo</Text>
                </Group>
                <Text size="sm" c="dimmed">
                  Operações administrativas do próprio audit (purge mensal, audit grants, verify-chain manual)
                  geram eventos <Code>audit:*</Code> — auditores podem inspecionar mudanças no próprio sistema.
                </Text>
              </Card>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="integrity">
            <Stack gap="md">
              <Title order={3}>Verificação de integridade</Title>
              <Text>
                Cada evento contém um SHA-256 calculado sobre o conteúdo (tenant, ator, action, payload, before/after, timestamp) <strong>+</strong> o hash do evento anterior do mesmo tenant. Quebrar qualquer elo invalida toda a cadeia subsequente.
              </Text>

              <Title order={4} mt="md">Endpoint manual</Title>
              <Code block style={{ fontSize: 13 }}>
                {`POST /api/v1/audit/verify-chain
Body: { "tenant_id": 5 }   // ou null para cross-tenant (auditor_global)`}
              </Code>

              <Text size="sm" c="dimmed">
                Retorna estatísticas da verificação: total de eventos verificados, primeiro hash inválido encontrado (se houver), eventos órfãos sem ancestral, amostra S3 (compara um batch arquivado contra o Postgres pra detectar drift).
              </Text>

              <Title order={4} mt="md">Job assíncrono</Title>
              <Text size="sm" c="dimmed">
                Verificação completa cross-tenant é cara. Em produção rodamos como job assíncrono (sprint
                futura) — por enquanto, a chamada é síncrona e pode demorar segundos a minutos dependendo do volume.
              </Text>

              <Alert color="red" icon={<IconInfoCircle size={18} />} title="Em caso de chain quebrada">
                Se a verificação encontrar quebra de chain: (1) preservar evidência do estado atual (snapshot
                Postgres + S3); (2) acionar SI/Compliance imediatamente; (3) NÃO tentar corrigir hashes
                manualmente — quebra é evidência forense por si só.
              </Alert>
            </Stack>
          </Tabs.Panel>
        </Box>
      </Tabs>
    </Stack>
  )
}
